import Reveal from "@/components/Reveal";
import { Link } from "react-router-dom";

type Step = {
    title: string;
    description: string;
};

const steps: Step[] = [
    {
        title: "Connect your store",
        description:
            "Plug in your Shopify store, website, product catalogue, and brand assets.",
    },
    {
        title: "Choose what to generate",
        description:
            "Try-ons, catalogues, reels, UGC, CGI, and product videos, you pick the mix.",
    },
    {
        title: "Set your schedule",
        description:
            "e.g. 3 creatives, 2 product videos, and 2 UGC ads, delivered every day at 9 AM.",
    },
    {
        title: "AI agents generate",
        description:
            "Your AI creative crew produces on-brand, conversion-ready assets automatically.",
    },
    {
        title: "Test, learn & improve",
        description:
            "We A/B test, analyse performance, and boost top performers for better ROAS.",
    },
    {
        title: "Delivered ready to use",
        description:
            "Ecommerce, ad, and social-ready for Amazon, Flipkart, Shopify, and more.",
    },
];

const stats = [
    "99% cheaper production",
    "Works 24/7",
    "100% brand-consistent",
];

const AutopilotSection = () => {
    return (
        <section className="py-12 sm:py-16 md:py-24">
            <div className="mx-auto max-w-6xl px-6">
                <Reveal variant="fade-up" className="text-center">
                    <div className="mb-4 inline-flex items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-widest text-primary">
                            Always-on automation
                        </span>
                        <span className="rounded-full border border-border bg-card px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                            Coming soon
                        </span>
                    </div>
                    <h2 className="mx-auto max-w-3xl text-2xl font-bold leading-tight text-foreground sm:text-3xl md:text-4xl">
                        Set it once. Get fresh creatives every day.
                    </h2>
                    <p className="mx-auto mt-4 max-w-2xl text-sm text-muted-foreground sm:text-base">
                        Connect your store, pick what to generate, and set a
                        schedule. Your always-on AI creative team produces,
                        tests, and delivers high-converting creatives for you,
                        every day, on autopilot.
                    </p>
                </Reveal>

                <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {steps.map((step, i) => (
                        <Reveal
                            key={step.title}
                            variant="fade-up"
                            delay={i * 90}
                            className="h-full"
                        >
                            <div className="h-full rounded-2xl border border-white/10 bg-card/40 p-5 backdrop-blur-sm transition-colors hover:border-primary/30">
                                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-primary/30 text-sm font-bold text-primary">
                                    {i + 1}
                                </span>
                                <h3 className="mt-4 text-base font-semibold text-foreground">
                                    {step.title}
                                </h3>
                                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                                    {step.description}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <Reveal variant="fade-up" delay={120}>
                    <div className="mt-10 flex flex-col items-center gap-6 text-center">
                        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
                            {stats.map((stat) => (
                                <span
                                    key={stat}
                                    className="inline-flex items-center gap-2 text-sm font-semibold text-foreground"
                                >
                                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                                    {stat}
                                </span>
                            ))}
                        </div>
                        <p className="max-w-xl text-sm text-muted-foreground">
                            Your brand never runs out of content. Your growth
                            never stops.
                        </p>
                        <Link
                            to="/contact?reason=Early%20Access&subject=Automation%20early%20access"
                            className="inline-block rounded-2xl bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground transition-all hover:opacity-90 sm:text-base"
                        >
                            Get early access
                        </Link>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export default AutopilotSection;
